/**
 * /api/eval3 — the new evals' own API module.
 *
 * Nothing here is imported from features/evals: the two eval systems share no
 * code, so that deleting the current one is a file delete. Same axios
 * instance as the rest of the app (that is app infrastructure, not eval code).
 *
 * Two calls go to endpoints that live under /evals on the server —
 * integrations and the model catalog. Those are the team's LLM credentials
 * and the global model list, not eval code: every model picker in the app
 * (agents, playground, prompts) reads them. When the old evals are deleted
 * those two routes move, and only this file changes.
 */
import axiosInstance from 'shared/services/api/axiosInstance';

const tparams = (teamId, extra = {}) => ({ params: { team_id: teamId, ...extra } });

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

export async function listLineages(teamId) {
  const { data } = await axiosInstance.get('/eval3/definitions', tparams(teamId));
  return data?.lineages || [];
}

/** The eval templates: { categories: [{id, label}], templates: [{id, name, description, when, adapt, target, category, uses, has_judge, applies_when, graph}] }. */
export async function listTemplates(teamId) {
  const { data } = await axiosInstance.get('/eval3/templates', tparams(teamId));
  return data;
}

/** One page of the evals list, newest first; `q` searches name, agent and description on the server. */
export async function listLineagesPage(teamId, { limit = 50, offset = 0, q = '' } = {}) {
  const { data } = await axiosInstance.get('/eval3/definitions', tparams(teamId, { limit, offset: offset || undefined, q: q || undefined }));
  return { lineages: data?.lineages || [], total: data?.total ?? (data?.lineages || []).length };
}

export async function getLineage(teamId, lineageId) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}`, tparams(teamId));
  return data; // { lineage, current, versions }
}

export async function getVersion(teamId, lineageId, version) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/versions/${version}`, tparams(teamId));
  return data; // { lineage, definition }
}

/** Create a lineage and its version 1. `definition` is { graph, applies_when?, ... }. */
export async function createLineage(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/definitions', body, tparams(teamId));
  return data; // { lineage, definition }
}

/** name / description / enabled — none of these make a new version. */
export async function patchLineage(teamId, lineageId, patch) {
  const { data } = await axiosInstance.patch(`/eval3/definitions/${lineageId}`, patch, tparams(teamId));
  return data?.lineage;
}

/** Delete an eval with its results and the alerts that watch it. */
export async function deleteLineage(teamId, lineageId) {
  const { data } = await axiosInstance.delete(`/eval3/definitions/${lineageId}`, tparams(teamId));
  return data;
}

/** Dry-run the validator: { ok, errors: [{node_id, rule, message}], warnings }. */
export async function validateGraph(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/definitions/validate', body, tparams(teamId));
  return data;
}

/**
 * The next frozen version. body: { change_note, based_on?, graph?, applies_when?, ... }.
 * Written, not running: nothing runs it until setProductionVersion.
 */
export async function createVersion(teamId, lineageId, body) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/versions`, body, tparams(teamId));
  return data; // { lineage, definition }
}

/**
 * Move the production label to a version — the one call that changes what
 * runs (TRO-461). Fires the eval's backfill policy; the response carries it.
 */
export async function setProductionVersion(teamId, lineageId, version) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/production`, { version }, tparams(teamId));
  return data; // { lineage, definition, previous_version, backfill }
}

export async function revertVersion(teamId, lineageId, version, changeNote) {
  const body = { version };
  if (changeNote) body.change_note = changeNote;
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/revert`, body, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// Builder: dry runs, things to test on, the schema rail
// ---------------------------------------------------------------------------

/**
 * Run a graph (or one node of it) over a real unit. Writes nothing.
 * body: { target, run_id | span_id | conversation_id, graph, applies_when?, only_node_id?, until_node_id? }
 */
/** How a Semantic check will read a statement: {kind: 'text'|'answered'|'repeated', negated}. */
export async function readStatement(teamId, statement) {
  const { data } = await axiosInstance.post('/eval3/semantic/reading', { statement }, tparams(teamId));
  return data;
}

/** What span variables read on recent runs: matches per run, an example, the span names. Reads only. */
export async function previewSpanVariables(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/definitions/span-variables/preview', body, tparams(teamId));
  return data;
}

export async function previewGraph(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/definitions/preview', body, tparams(teamId));
  return data;
}

export async function listUnits(teamId, { target, agentName, q, limit } = {}) {
  const { data } = await axiosInstance.get('/eval3/units', tparams(teamId, {
    target, agent_name: agentName || undefined, q: q || undefined, limit: limit || undefined,
  }));
  return data?.units || [];
}

export async function getSchema(teamId, target) {
  const { data } = await axiosInstance.get('/eval3/schema', tparams(teamId, { target }));
  return data; // { target, groups: [{id, label, fields: [{path, type, label, python, prompt}]}] }
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export async function listRuns(teamId, lineageId, { state, passed, version, days, limit, offset, through = [] } = {}) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/runs`, tparams(teamId, {
    state: state || undefined, passed: passed == null ? undefined : String(passed), version: version || undefined,
    days: days || undefined, limit: limit || undefined, offset: offset || undefined, ...scope(through),
  }));
  return data; // { total, limit, offset, runs }
}

/** `through`: step ids; scopes every number to runs whose path went through all of them. */
const scope = (through) => (Array.isArray(through) && through.length ? { through: through.join(',') } : {});

export async function getStats(teamId, lineageId, days = 30, through = [], version = null) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/stats`, tparams(teamId, { days, ...scope(through), version: version || undefined }));
  return data; // { totals, by_day, by_version, skips }
}

export async function getRun(teamId, runId) {
  const { data } = await axiosInstance.get(`/eval3/runs/${runId}`, tparams(teamId));
  return data; // { run, steps, queue }
}

// ---------------------------------------------------------------------------
// Verdicts on units — the run / span / conversation views (TRO-451)
// ---------------------------------------------------------------------------

/** Newest verdict per (eval, unit), for list columns. */
export async function listVerdicts(teamId, target, ids) {
  if (!ids || !ids.length) return [];
  const { data } = await axiosInstance.get('/eval3/verdicts', tparams(teamId, { target, ids: ids.join(',') }));
  return data?.verdicts || [];
}

/** Every verdict on one unit, grouped by eval, with journals: { lineages, thread_runs }. */
export async function getUnitVerdicts(teamId, target, id) {
  const { data } = await axiosInstance.get(`/eval3/units/${target}/${encodeURIComponent(id)}/verdicts`, tparams(teamId));
  // `spans`: a run's span verdicts; `thread_runs[].spans`: those of each run in a conversation.
  return {
    lineages: data?.lineages || [],
    spans: data?.spans || [],
    thread_runs: (data?.thread_runs || []).map((r) => ({ ...r, spans: r.spans || [] })),
  };
}

// ---------------------------------------------------------------------------
// Was this verdict right? (TRO-415, v3 verdicts only)
// ---------------------------------------------------------------------------

/** corrected_passed and reason_text are required on a thumbs-down and refused on a thumbs-up. */
export async function submitFeedback(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/feedback', { ...body, source: 'v3' }, tparams(teamId));
  return data?.feedback;
}

/** The caller's own opinions on these v3 runs, keyed by eval run id. */
export async function getMyFeedback(teamId, evalRunIds) {
  if (!evalRunIds || !evalRunIds.length) return {};
  const { data } = await axiosInstance.get('/eval3/feedback', tparams(teamId, { source: 'v3', ids: evalRunIds.join(',') }));
  return Object.fromEntries((data?.feedback || []).map((f) => [String(f.eval_run_id), f]));
}

export async function retractFeedback(teamId, id) {
  const { data } = await axiosInstance.delete(`/eval3/feedback/${id}`, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// The grading queue (TRO-428)
// ---------------------------------------------------------------------------

export async function listQueue(teamId, { status = 'open', lineageId, limit, offset, through = [] } = {}) {
  const { data } = await axiosInstance.get('/eval3/queue', tparams(teamId, {
    status, lineage_id: lineageId || undefined, limit: limit || undefined, offset: offset || undefined, ...scope(through),
  }));
  return data?.items || [];
}

/**
 * Both queues: a graph's own human steps, and the audit escalations.
 *
 * The audit fields have to be carried through here or the Spot checks picker
 * reads zero for ever — which looked correct the day it shipped only because
 * the real total happened to be 0.
 */
export async function getQueueCounts(teamId) {
  const { data } = await axiosInstance.get('/eval3/queue/counts', tparams(teamId));
  return {
    counts: data?.counts || [], total: data?.total || 0,
    audit_counts: data?.audit_counts || [], audit_total: data?.audit_total || 0,
  };
}

/** { item, options: { field, values, instructions }, path, target } */
export async function getQueueItem(teamId, id) {
  const { data } = await axiosInstance.get(`/eval3/queue/${id}`, tparams(teamId));
  return data;
}

/** { item, resume_enqueued } | { item, already_graded: true } */
export async function gradeQueueItem(teamId, id, choice) {
  const { data } = await axiosInstance.post(`/eval3/queue/${id}/grade`, { choice }, tparams(teamId));
  return data;
}

/**
 * The same answer to a selection of items.
 * { graded: [id], already_graded: [id], skipped: [{ id, error, message }] }
 */
export async function gradeQueueItems(teamId, ids, choice) {
  const { data } = await axiosInstance.post('/eval3/queue/grade', { ids, choice }, tparams(teamId));
  return { graded: data?.graded || [], already_graded: data?.already_graded || [], skipped: data?.skipped || [] };
}

// ---------------------------------------------------------------------------
// Backfills — re-scoring history when a version changes (TRO-431)
// ---------------------------------------------------------------------------

/** One per version: status, estimate, progress, runs / units / spend written so far. */
export async function listBackfills(teamId, lineageId) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/backfills`, tparams(teamId));
  return data?.backfills || [];
}

export async function confirmBackfill(teamId, id) {
  const { data } = await axiosInstance.post(`/eval3/backfills/${id}/confirm`, {}, tparams(teamId));
  return data?.backfill;
}

export async function cancelBackfill(teamId, id) {
  const { data } = await axiosInstance.post(`/eval3/backfills/${id}/cancel`, {}, tparams(teamId));
  return data?.backfill;
}

// ---------------------------------------------------------------------------
// Blast radius — what a change would have done to recent verdicts (TRO-432)
// ---------------------------------------------------------------------------

/** { changed, total, population, sampled, flips_pass_to_fail, flips_fail_to_pass, unknown, examples } | { refused, reason, message } */
export async function previewBlastRadius(teamId, lineageId, graph, days = 30) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/blast-radius`, { graph, days }, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// Proposals — changes the calibration engine suggests, for a person to decide (TRO-435)
// ---------------------------------------------------------------------------

/** → { items, counts: { open, needs_human, auto_promoted, approved, rejected, superseded } } */
export async function listProposals(teamId, { status, lineageId } = {}) {
  const { data } = await axiosInstance.get('/eval3/proposals', tparams(teamId, { status: status || undefined, lineage_id: lineageId || undefined }));
  return { items: data?.items || [], counts: data?.counts || {} };
}

/** → { proposal, base, current, proposed_graph, evidence, evidence_total, examples: { pass_to_fail, fail_to_pass, regressions } } */
export async function getProposal(teamId, id) {
  const { data } = await axiosInstance.get(`/eval3/proposals/${id}`, tparams(teamId));
  return data;
}

/** → { proposal, version, backfill } */
export async function approveProposal(teamId, id, { note } = {}) {
  const { data } = await axiosInstance.post(`/eval3/proposals/${id}/approve`, note ? { note } : {}, tparams(teamId));
  return data;
}

export async function rejectProposal(teamId, id, reason) {
  const { data } = await axiosInstance.post(`/eval3/proposals/${id}/reject`, { reason }, tparams(teamId));
  return data;
}

/** → { proposal, version, backfill } */
export async function revertProposal(teamId, id) {
  const { data } = await axiosInstance.post(`/eval3/proposals/${id}/revert`, {}, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// Canvas layout — where the steps sit; never a new version (TRO-449)
// ---------------------------------------------------------------------------

/** ui: { layout: { [nodeId]: { x, y } } } — stored on that version, in place. */
export async function saveCanvasLayout(teamId, lineageId, version, ui) {
  const { data } = await axiosInstance.patch(`/eval3/definitions/${lineageId}/versions/${version}/ui`, { ui }, tparams(teamId));
  return data?.definition || null;
}

// ---------------------------------------------------------------------------
// Discovery — file failing results with something in common as a pattern (TRO-438)
// ---------------------------------------------------------------------------

/** → { pattern_id, instances_attached, instance_count, threshold, emitted, left_out } */
export async function fileFinding(teamId, { lineageId, evalRunIds, title, question } = {}) {
  const { data } = await axiosInstance.post('/eval3/discovery/findings', {
    lineage_id: lineageId, eval_run_ids: evalRunIds, title, ...(question ? { question } : {}),
  }, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// Telemetry — reliability and cost for one eval (TRO-430)
// ---------------------------------------------------------------------------

/** { reliability: { series, by_version, seams, states, screen_error }, efficiency: { path_distribution, judge_traffic, latency, units_and_spend, cost_per_verdict } } */
/** Series come back per hour of `tz`'s clock ('YYYY-MM-DDTHH'); the page groups them. */
export async function getTelemetry(teamId, lineageId, days = 30, through = [], tz, version = null) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/telemetry`, tparams(teamId, { days, ...scope(through), tz: tz || undefined, version: version || undefined }));
  return data;
}

/** Every version side by side over the window: outcomes, cost, latency, what people said, flips on the same units. */
export async function compareVersions(teamId, lineageId, days = 30, through = []) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/compare`, tparams(teamId, { days, ...scope(through) }));
  return data; // { versions, head_to_head, production_version }
}

/** Every path, and per step its outcomes, routes, outcomes by day and cost — scoped like getTelemetry. */
export async function getSteps(teamId, lineageId, days = 30, through = [], tz, version = null) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/steps`, tparams(teamId, { days, ...scope(through), tz: tz || undefined, version: version || undefined }));
  return data;
}

// ---------------------------------------------------------------------------
// Screen error and audit rulings (TRO-440)
// ---------------------------------------------------------------------------

/** One eval: { measurable, screen_error: {rate, ci95}, refined_screen_error, rulings, by_screen, trend, sla }. */
export async function getSla(teamId, lineageId, days = 30) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/sla`, tparams(teamId, { days }));
  return data;
}

export async function getTeamSla(teamId, days = 30) {
  const { data } = await axiosInstance.get('/eval3/sla', tparams(teamId, { days }));
  return data;
}

/** Disagreements with both verdicts and the journal. */
export async function listDisagreements(teamId, { status = 'open', lineageId, limit, offset } = {}) {
  const { data } = await axiosInstance.get('/eval3/audit/disagreements', tparams(teamId, {
    status, lineage_id: lineageId || undefined, limit: limit || undefined, offset: offset || undefined,
  }));
  // The array, with how many there are in all beside it.
  return Object.assign(data?.items || [], { total: data?.total });
}

/** right: 'screen' | 'full'. → { item, pattern_detection } | { item, already_ruled: true } */
export async function ruleDisagreement(teamId, id, right) {
  const { data } = await axiosInstance.post(`/eval3/audit/disagreements/${id}/rule`, { right }, tparams(teamId));
  return data;
}

// ---------------------------------------------------------------------------
// Team context the editor needs: agents, LLM credentials, the model catalog
// ---------------------------------------------------------------------------

export async function listAgents(teamId) {
  const { data } = await axiosInstance.get('/agent-runs/agents', tparams(teamId));
  return data?.agents || [];
}

/** The team's LLM credentials: [{ id, provider, label, key_prefix, config: { models }, default_model }]. */
export async function listIntegrations(teamId) {
  const { data } = await axiosInstance.get('/evals/integrations', tparams(teamId));
  return data?.integrations || [];
}

/** Catalog models for one provider: [{ id, label, ... }]. */
export async function listModelCatalog(teamId, provider) {
  const { data } = await axiosInstance.get('/evals/model-catalog', tparams(teamId, { provider }));
  return data?.models || [];
}

// ---------------------------------------------------------------------------
// Self-improving — an eval alert + an agent that improves the eval when it fires
// ---------------------------------------------------------------------------

/** → { live, paused, pairs: [{ alert, agent, improves }], defaults: { instructions, condition } } */
export async function getSelfImproving(teamId, lineageId) {
  const { data } = await axiosInstance.get(`/eval3/definitions/${lineageId}/self-improving`, tparams(teamId));
  return data;
}

/** { measure, operator, threshold, window_seconds, instructions?, paused? } → the same shape as getSelfImproving */
export async function deploySelfImproving(teamId, lineageId, body) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/self-improving`, body, tparams(teamId));
  return data;
}

/** Edit the deployed pair in place: { measure?, operator?, threshold?, window_seconds?, instructions? } → the same shape as getSelfImproving */
export async function updateSelfImproving(teamId, lineageId, body) {
  const { data } = await axiosInstance.patch(`/eval3/definitions/${lineageId}/self-improving`, body, tparams(teamId));
  return data;
}

/** Run the self-improving agent now with the alert's current value. → { execution_id, agent_id, alert_value } */
export async function runSelfImprovingNow(teamId, lineageId) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/self-improving/run`, {}, tparams(teamId));
  return data;
}

/** Turn it off for good: the eval's managed alert and agent are deleted together. → the same shape as getSelfImproving */
export async function deleteSelfImproving(teamId, lineageId) {
  const { data } = await axiosInstance.delete(`/eval3/definitions/${lineageId}/self-improving`, tparams(teamId));
  return data;
}

export async function setSelfImprovingLive(teamId, lineageId, live) {
  const { data } = await axiosInstance.post(`/eval3/definitions/${lineageId}/self-improving/live`, { live }, tparams(teamId));
  return data;
}

// ── node-level feedback (datasets phase 6) ───────────────────────────────────

/** Several verdict thumbs at once: { feedback: [rows], skipped: [{ index, eval_run_id, error, message }] }. ≤ 200. */
export async function submitFeedbackBulk(teamId, items) {
  const { data } = await axiosInstance.post('/eval3/feedback/bulk', { items }, tparams(teamId));
  return data;
}

/** A correction of ONE STEP of a verdict, typed to the node: { eval_run_id, node_id, should_have_been, reason?, decision_id? }. */
export async function submitNodeFeedback(teamId, body) {
  const { data } = await axiosInstance.post('/eval3/node-feedback', body, tparams(teamId));
  return data?.node_feedback;
}

/** Several step corrections at once: { labelled: [rows], skipped: [...] }. ≤ 200. */
export async function submitNodeFeedbackBulk(teamId, items) {
  const { data } = await axiosInstance.post('/eval3/node-feedback/bulk', { items }, tparams(teamId));
  return data;
}

/** The team's step labels on some verdicts: { [eval_run_id]: [rows] }. */
export async function getNodeFeedback(teamId, evalRunIds) {
  return (await getNodeFeedbackPage(teamId, evalRunIds)).labels;
}

/** The same, plus the caller's user id (`me`) so the client can tell its own labels from the team's. */
export async function getNodeFeedbackPage(teamId, evalRunIds) {
  if (!evalRunIds || !evalRunIds.length) return { labels: {}, me: null };
  const { data } = await axiosInstance.get('/eval3/node-feedback', tparams(teamId, { eval_run_ids: evalRunIds.join(',') }));
  return { labels: data?.node_feedback || {}, me: data?.user_id ?? null };
}

export async function retractNodeFeedback(teamId, id) {
  const { data } = await axiosInstance.delete(`/eval3/node-feedback/${encodeURIComponent(id)}`, tparams(teamId));
  return data;
}
